import * as React from 'react';
import { Color, SpaceScale } from '../../theme/tokens';
import { useTheme } from '../../theme';
import { CSSRulesFunction, ResponsiveValue } from '../../theme/types';
import { omit } from '../../utils';
import { iconRegistry } from './registry';
import classNames from 'classnames';

export type IconNames = keyof typeof iconRegistry;

type HTMLProps = Omit<
  React.HTMLAttributes<SVGSVGElement>,
  keyof AppearanceProps & 'children'
>;

export enum IconVariant {
  Inherit = 'Inherit',
  Default = 'Default',
  Actionable = 'Actionable',
  Confirmation = 'Confirmation',
  Informative = 'Informative',
  Error = 'Error',
}

interface AppearanceProps {
  name: IconNames;
  size?: ResponsiveValue<SpaceScale>;
  variant?: IconVariant;
}

const iconColor: Record<IconVariant, Color> = {
  Inherit: Color.Inherit,
  Default: Color.ColdGrey_800,
  Actionable: Color.Secondary_400,
  Confirmation: Color.Success_800,
  Informative: Color.Informative_900,
  Error: Color.Error_800,
};

export interface IconProps extends AppearanceProps, HTMLProps {}

const iconStyle: CSSRulesFunction<AppearanceProps> = (theme, props) => {
  const { variant = IconVariant.Default, size = 6 } = props;
  const color = theme.color(iconColor[variant]);
  const scale = theme.responsiveSpacing(size, ['width', 'height']);

  return [
    {
      userSelect: 'none',
      overflow: 'hidden',
      flexShrink: 0,
      textAlign: 'center',
    },
    {
      color: color,
    },
    scale,
  ];
};

const Icon: React.FC<IconProps> = (props) => {
  const { theme } = useTheme();
  const { name } = props;
  const rest = omit(props, ['name', 'size', 'variant']);
  const Svg = iconRegistry[name];

  return (
    <Svg
      css={iconStyle(theme, props)}
      {...rest}
      // If an svg has text we want it to be ignored by GoogleTranslation
      className={classNames(rest.className, 'notranslate')}
      aria-hidden
      data-testid={name}
      focusable={false}
      // We expect all Icons to be 24px by 2px
      viewBox="0 0 24 24"
      // We remove width, height and fill so we can
      // drive it by the css and rely on viewBox
      width={undefined}
      height={undefined}
      fill={undefined}
    />
  );
};

export default Icon;
